import { Injectable, inject } from '@angular/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { MicrosoftAuthApi } from '../apis/microsoft-auth-api';

/**
 * Servizio che gestisce il login tramite Microsoft Entra ID (Azure AD).
 * Usa @azure/msal-browser per il flusso popup e SSO silenzioso.
 *
 * NON importa nulla da `@sia/auth` — restituisce i dati grezzi dal backend
 * e lascia al componente chiamante la gestione del post-login.
 */
@Injectable({ providedIn: 'root' })
export class MicrosoftLoginService {
  #api = inject(MicrosoftAuthApi);
  #deviceService = inject(DeviceDetectorService);

  #msalInstance: any = null;
  #config: { clientId: string; tenantId: string } | null = null;

  /**
   * Verifica se il login Microsoft e disponibile (configurato lato backend).
   * Restituisce true se clientId e tenantId sono configurati.
   */
  async checkAvailability(): Promise<boolean> {
    try {
      const config = await this.#api.getMicrosoftLoginConfig();
      if (config?.clientId && config?.tenantId) {
        this.#config = config;
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  /**
   * Inizializza lazily l'istanza MSAL PublicClientApplication.
   */
  async #ensureMsalInitialized(): Promise<any> {
    if (this.#msalInstance) {
      return this.#msalInstance;
    }

    if (!this.#config) {
      throw new Error('Microsoft login config not loaded');
    }

    const msal = await import('@azure/msal-browser');
    const msalConfig = {
      auth: {
        clientId: this.#config.clientId,
        authority: `https://login.microsoftonline.com/${this.#config.tenantId}`,
        redirectUri: window.location.origin,
      },
      cache: {
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: false,
      },
    };

    this.#msalInstance = new msal.PublicClientApplication(msalConfig);
    await this.#msalInstance.initialize();
    return this.#msalInstance;
  }

  /**
   * Costruisce l'oggetto deviceInfo dal DeviceDetectorService.
   */
  #buildDeviceInfo(): any {
    const di = this.#deviceService.getDeviceInfo();
    return {
      browser: di.browser,
      browserVersion: di.browser_version,
      device: di.device,
      deviceType: di.deviceType,
      orientation: di.orientation,
      os: di.os,
      osVersion: di.os_version,
      userAgent: di.userAgent,
    };
  }

  /**
   * Apre il popup Microsoft per il login interattivo.
   * Restituisce l'oggetto grezzo dal backend (token, identifier, twoFactorType, errors, messages)
   * oppure null in caso di errore.
   */
  async loginPopup(): Promise<any | null> {
    try {
      const msalApp = await this.#ensureMsalInitialized();
      const loginResponse = await msalApp.loginPopup({
        scopes: ['openid', 'profile', 'email'],
        prompt: 'select_account',
      });

      if (loginResponse?.idToken) {
        return await this.#api.loginWithMicrosoftIdToken(
          loginResponse.idToken,
          this.#buildDeviceInfo(),
        );
      }
      return null;
    } catch (error: any) {
      // BrowserAuthError con errorCode 'user_cancelled' è normale: cancellazione silenziosa
      if (error?.errorCode === 'user_cancelled') {
        return null;
      }
      // Qualsiasi altro errore (HTTP 401, token non valido, ecc.) viene ri-lanciato
      // in modo che il chiamante possa mostrare un notify di errore appropriato.
      throw error;
    }
  }

  /**
   * Prova il SSO silenzioso (senza interazione utente).
   * Restituisce l'oggetto grezzo dal backend oppure null se non possibile.
   */
  async ssoSilent(): Promise<any | null> {
    try {
      const msalApp = await this.#ensureMsalInitialized();
      const accounts = msalApp.getAllAccounts();

      if (accounts.length === 0) {
        return null;
      }

      const silentResponse = await msalApp.acquireTokenSilent({
        scopes: ['openid', 'profile', 'email'],
        account: accounts[0],
      });

      if (silentResponse?.idToken) {
        return await this.#api.loginWithMicrosoftIdToken(
          silentResponse.idToken,
          this.#buildDeviceInfo(),
        );
      }
      return null;
    } catch (error: any) {
      // InteractionRequiredAuthError e atteso — serve login interattivo
      if (error?.name === 'InteractionRequiredAuthError') {
        return null;
      }
      console.error('Microsoft SSO silent error:', error);
      return null;
    }
  }
}
